import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { ENVIRONMENT } from '../environment/environment.token';
import { HttpService } from '../service/http.service';

export interface AuthCredentials {
  username: string;
  password: string;
}

interface LoginTokenResponse {
  access_token: string;
  token_type: string;
  user: UserInfoResponse;
}

interface UserInfoResponse {
  id: string;
  email: string;
  name: string;
  is_active: boolean;
}

export interface RegisterUserPayload {
  name: string;
  email: string;
  password: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private static readonly STORAGE_TOKEN_KEY = 'auth.accessToken';
  private static readonly STORAGE_USER_NAME_KEY = 'auth.userName';
  private static readonly STORAGE_USER_ID_KEY = 'auth.userId';

  private readonly http = inject(HttpClient);
  private readonly httpService = inject(HttpService);
  private readonly router = inject(Router);
  private readonly environment = inject(ENVIRONMENT);

  private readonly _accessToken = signal<string | null>(this.readPersistedToken());
  private readonly _isAuthenticated = signal(Boolean(this._accessToken()));
  private readonly _userName = signal<string>(this.readPersistedUserName());
  private readonly _userId = signal<string>(this.readPersistedUserId());

  readonly isAuthenticated = computed(() => this._isAuthenticated());
  readonly userName = computed(() => this._userName());
  readonly userId = computed(() => this._userId());

  login(credentials: AuthCredentials): Observable<boolean> {
    return this.httpService.genericPost<LoginTokenResponse>('auth/login', {
      username: credentials.username,
      password: credentials.password,
    }).pipe(
        tap(response => this.setSession(response)),
        map(response => response.access_token?.trim() ?? ''),
        map(token => token.length > 0),
        catchError(() => {
          this.clearSession();
          return of(false);
        }),
      );
  }

  /**
   * Autentica via ticket externo do MApE² (GET auth/mape/login?ticket=...).
   * O retorno é idêntico ao login padrão; a sessão é estabelecida da mesma forma.
   */
  loginWithTicket(ticket: string): Observable<boolean> {
    return this.httpService.genericGetParams<LoginTokenResponse>('auth/mape/login', [
      { param: 'ticket', value: ticket },
    ]).pipe(
      tap(response => this.setSession(response)),
      map(response => response.access_token?.trim() ?? ''),
      map(token => token.length > 0),
      catchError(() => {
        this.clearSession();
        return of(false);
      }),
    );
  }

  register(payload: RegisterUserPayload): Observable<boolean> {
    return this.http.post(`${this.environment.apiUrl}auth/register`, payload).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  getAccessToken(): string | null {
    return this._accessToken();
  }

  getAuthorizationToken(): string | null {
    return this.getAccessToken();
  }

  getUserName(): string {
    return this._userName();
  }

  getUserId(): string {
    return this._userId();
  }

  logout(redirectToLogin = true): void {
    this.clearSession();
    if (!redirectToLogin) {
      return;
    }
    this.router.navigate(['/login']);
  }

  private setSession(response: LoginTokenResponse): void {
    const token = response.access_token?.trim() ?? '';
    const name = response.user.name?.trim() ?? '';
    const userId = response.user.id?.trim() ?? '';

    this._accessToken.set(token);
    this._isAuthenticated.set(token.length > 0);
    this._userName.set(name);
    this._userId.set(userId);
    sessionStorage.setItem(AuthService.STORAGE_TOKEN_KEY, token);
    sessionStorage.setItem(AuthService.STORAGE_USER_NAME_KEY, name);
    sessionStorage.setItem(AuthService.STORAGE_USER_ID_KEY, userId);
  }

  private clearSession(): void {
    this._accessToken.set(null);
    this._isAuthenticated.set(false);
    this._userName.set('');
    this._userId.set('');
    sessionStorage.removeItem(AuthService.STORAGE_TOKEN_KEY);
    sessionStorage.removeItem(AuthService.STORAGE_USER_NAME_KEY);
    sessionStorage.removeItem(AuthService.STORAGE_USER_ID_KEY);
  }

  private readPersistedToken(): string | null {
    const token = sessionStorage.getItem(AuthService.STORAGE_TOKEN_KEY)?.trim() ?? '';
    if (!token) {
      return null;
    }
    if (this.isExpiredToken(token)) {
      sessionStorage.removeItem(AuthService.STORAGE_TOKEN_KEY);
      return null;
    }
    return token;
  }

  private readPersistedUserName(): string {
    return sessionStorage.getItem(AuthService.STORAGE_USER_NAME_KEY)?.trim() ?? '';
  }

  private readPersistedUserId(): string {
    return sessionStorage.getItem(AuthService.STORAGE_USER_ID_KEY)?.trim() ?? '';
  }

  private isExpiredToken(token: string): boolean {
    try {
      const payload = token.split('.')[1];
      if (!payload) {
        return false;
      }
      const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
      const decodedPayload = JSON.parse(atob(normalizedPayload)) as { exp?: number };
      if (!decodedPayload.exp) {
        return false;
      }
      return decodedPayload.exp * 1000 <= Date.now();
    } catch {
      return false;
    }
  }
}
